import React from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, useLocation, Navigate } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import { AuthProvider } from "@/contexts/AuthContext";
import { LanguageProvider } from "@/contexts/LanguageContext";
import AuthCallback from "@/pages/AuthCallback";
import Dashboard from "@/pages/Dashboard";
import Generator from "@/pages/Generator";
import Library from "@/pages/Library";
import History from "@/pages/History";
import Market from "@/pages/Market";
import HiddenGems from "@/pages/HiddenGems";
import Predictions from "@/pages/Predictions";
import Captain from "@/pages/Captain";
import Tracker from "@/pages/Tracker";
import Mapping from "@/pages/Mapping";
import Planner from "@/pages/Planner";
import Submit from "@/pages/Submit";
import FdrHeatmap from "@/pages/FdrHeatmap";
import RotationPairFinder from "@/pages/RotationPairFinder";
import RotationFdrFinder from "@/pages/RotationFdrFinder";
import Blog from "@/pages/Blog";
import BlogPost from "@/pages/BlogPost";
import BlogAdmin from "@/pages/BlogAdmin";
import FocusLists from "@/pages/FocusLists";
import FocusListDetail from "@/pages/FocusListDetail";
import FocusWizard from "@/pages/FocusWizard";
import IncomingRadar from "@/pages/IncomingRadar";
import Preseason from "@/pages/Preseason";
import PreseasonDatabase from "@/pages/PreseasonDatabase";
import PreseasonVsReality from "@/pages/PreseasonVsReality";
import PreseasonClub from "@/pages/PreseasonClub";
import Hlidac from "@/pages/Hlidac";
import ClubOpportunities from "@/pages/ClubOpportunities";
import Profile from "@/pages/Profile";
import Leaderboard from "@/pages/Leaderboard";
import MarketChallenge from "@/pages/MarketChallenge";
import TransferDeskHub from "@/pages/TransferDeskHub";
import LineupSim from "@/pages/LineupSim";
import LineupSimShare from "@/pages/LineupSimShare";
import LineupSimH2H from "@/pages/LineupSimH2H";
import LineupSimTopPicks from "@/pages/LineupSimTopPicks";
import WorldCupHub from "@/pages/WorldCupHub";
import WorldCupTeam from "@/pages/WorldCupTeam";
import WorldCupPlayers from "@/pages/WorldCupPlayers";
import WorldCupStrategy from "@/pages/WorldCupStrategy";
import WorldCupStandings from "@/pages/WorldCupStandings";
import WorldCupDarkHorses from "@/pages/WorldCupDarkHorses";
import WorldCupMatchday from "@/pages/WorldCupMatchday";
import AdminRoundCall from "@/pages/AdminRoundCall";
import AdminDuplicates from "@/pages/AdminDuplicates";
import AdminPreseasonData from "@/pages/AdminPreseasonData";
import Inko from "@/pages/Inko";
import InkoDaily from "@/pages/InkoDaily";
import TimingAnalyzer, { TAB_ROUTES } from "@/pages/TimingAnalyzer";
import AdminAnalytics from "@/pages/AdminAnalytics";
import AnalyticsTracker from "@/components/AnalyticsTracker";
import Home from "@/pages/Home";
import Players from "@/pages/Players";
import PlayerProfile from "@/pages/PlayerProfile";
import Fun from "@/pages/Fun";
import LabLayout from "@/pages/lab/LabLayout";
import LabAnalyze from "@/pages/lab/LabAnalyze";
import LabScout from "@/pages/lab/LabScout";
import LabSelect from "@/pages/lab/LabSelect";
import LabPlay from "@/pages/lab/LabPlay";
import LabReview from "@/pages/lab/LabReview";
import Academy from "@/pages/Academy";
import Bksa from "@/pages/Bksa";
import StatBattle from "@/pages/StatBattle";
import HigherLower from "@/pages/HigherLower";
import PriceGuess from "@/pages/PriceGuess";
import PlayerFinder from "@/pages/PlayerFinder";
import ScoutHub from "@/pages/ScoutHub";
import RecruitmentFocus from "@/pages/RecruitmentFocus";

// Legacy /timing?tab=... links → standalone tool routes.
function TimingRedirect() {
    const location = useLocation();
    const tab = new URLSearchParams(location.search).get("tab");
    return <Navigate to={tab ? (TAB_ROUTES[tab] || "/lab/analyze") : "/lab/analyze"} replace />;
}

function AppRoutes() {
    const location = useLocation();
    // CRITICAL: Detect session_id synchronously during render to avoid race conditions.
    // Emergent auth returns the user at {origin}/#session_id=... — handle that before
    // any normal routing or auth check.
    if (location.hash?.includes("session_id=")) {
        return <AuthCallback />;
    }
    return (
        <Routes>
            <Route path="/" element={<Home />} />
            <Route path="/dashboard" element={<Dashboard />} />
            <Route path="/lab" element={<LabLayout />}>
                <Route index element={<Navigate to="/lab/analyze" replace />} />
                <Route path="analyze" element={<LabAnalyze />} />
                <Route path="scout" element={<LabScout />} />
                <Route path="select" element={<LabSelect />} />
                <Route path="play" element={<LabPlay />} />
                <Route path="review" element={<LabReview />} />
            </Route>
            <Route path="/players" element={<Players />} />
            <Route path="/players/:key" element={<PlayerProfile />} />
            <Route path="/fun" element={<Fun />} />
            <Route path="/fun/stat-battle" element={<StatBattle />} />
            <Route path="/fun/higher-lower" element={<HigherLower />} />
            <Route path="/fun/price-guess" element={<PriceGuess />} />
            <Route path="/bksa" element={<Bksa />} />
            <Route path="/academy" element={<Academy />} />
            <Route path="/academy/:slug" element={<BlogPost />} />
            <Route path="/tools/season-dna" element={<TimingAnalyzer fixedTab="pool" />} />
            <Route path="/tools/player-explorer" element={<Navigate to="/players" replace />} />
            <Route path="/tools/compare" element={<TimingAnalyzer fixedTab="compare" />} />
            <Route path="/tools/finders" element={<TimingAnalyzer fixedTab="finders" />} />
            <Route path="/tools/fixture-matrix" element={<TimingAnalyzer fixedTab="matrix" />} />
            <Route path="/tools/season-simulator" element={<TimingAnalyzer fixedTab="simulator" />} />
            <Route path="/tools/transfers" element={<TimingAnalyzer fixedTab="transfers" />} />
            <Route path="/tools/hauls" element={<TimingAnalyzer fixedTab="hauls" />} />
            <Route path="/tools/fdr-analyzer" element={<TimingAnalyzer fixedTab="fdrperf" />} />
            <Route path="/tools/player-finder" element={<PlayerFinder />} />
            <Route path="/tools/recruitment-focus" element={<RecruitmentFocus />} />
            <Route path="/fokus" element={<FocusLists />} />
            <Route path="/fokus/novy" element={<FocusWizard />} />
            <Route path="/fokus/:slug" element={<FocusListDetail />} />
            <Route path="/scout" element={<ScoutHub />} />
            <Route path="/generator" element={<Generator />} />
            <Route path="/market" element={<Market />} />
            <Route path="/hidden-gems" element={<HiddenGems />} />
            <Route path="/predictions" element={<Predictions />} />
            <Route path="/captain" element={<Captain />} />
            <Route path="/tracker" element={<Tracker />} />
            <Route path="/mapping" element={<Mapping />} />
            <Route path="/planner" element={<Planner />} />
            <Route path="/submit" element={<Submit />} />
            <Route path="/fdr" element={<FdrHeatmap />} />
            <Route path="/rotation" element={<RotationPairFinder />} />
            <Route path="/rotation-fdr" element={<RotationFdrFinder />} />
            <Route path="/blog" element={<Navigate to="/academy" replace />} />
            <Route path="/radar" element={<IncomingRadar />} />
            <Route path="/preseason" element={<Preseason />} />
            <Route path="/hlidac" element={<Hlidac />} />
            <Route path="/academy/serie/club-opportunities" element={<ClubOpportunities />} />
            <Route path="/preseason/database" element={<PreseasonDatabase />} />
            <Route path="/preseason/vs-reality" element={<PreseasonVsReality />} />
            <Route path="/preseason/club/:key" element={<PreseasonClub />} />
            <Route path="/blog/admin" element={<BlogAdmin />} />
            <Route path="/blog/:slug" element={<BlogPost />} />
            <Route path="/challenges" element={<Library />} />
            <Route path="/generations" element={<History />} />
            <Route path="/u/:userId" element={<Profile />} />
            <Route path="/leaderboard" element={<Leaderboard />} />
            <Route path="/play/market/:cid" element={<MarketChallenge />} />
            <Route path="/transfer-desk" element={<TransferDeskHub />} />
            <Route path="/lineup-sim" element={<LineupSim />} />
            <Route path="/lineup-sim/top-picks" element={<LineupSimTopPicks />} />
            <Route path="/lineup-sim/share/:run_id" element={<LineupSimShare />} />
            <Route path="/lineup-sim/h2h/:challenge_id" element={<LineupSimH2H />} />
            <Route path="/worldcup" element={<WorldCupHub />} />
            <Route path="/worldcup/team/:teamId" element={<WorldCupTeam />} />
            <Route path="/worldcup/players" element={<WorldCupPlayers />} />
            <Route path="/worldcup/strategy" element={<WorldCupStrategy />} />
            <Route path="/worldcup/standings" element={<WorldCupStandings />} />
            <Route path="/worldcup/dark-horses" element={<WorldCupDarkHorses />} />
            <Route path="/worldcup/matchday/:md" element={<WorldCupMatchday />} />
            <Route path="/play/round-call/:md" element={<WorldCupMatchday minimal />} />
            <Route path="/worldcup/admin/round-call" element={<AdminRoundCall />} />
            <Route path="/inko" element={<Inko />} />
            <Route path="/inko/daily" element={<InkoDaily />} />
            <Route path="/inko/daily-10" element={<Inko />} />
            <Route path="/timing" element={<TimingRedirect />} />
            <Route path="/admin/duplicates" element={<AdminDuplicates />} />
            <Route path="/admin/preseason-data" element={<AdminPreseasonData />} />
            <Route path="/admin/analytics" element={<AdminAnalytics />} />
        </Routes>
    );
}

function App() {
    return (
        <div className="App">
            <AuthProvider>
            <LanguageProvider>
                <BrowserRouter>
                    <AnalyticsTracker />
                    <AppRoutes />
                </BrowserRouter>
                <Toaster
                    theme="dark"
                    position="bottom-right"
                    toastOptions={{
                        style: {
                            background: "#121212",
                            color: "#ffffff",
                            border: "1px solid rgba(255,255,255,0.1)",
                            borderRadius: "0",
                            fontFamily: "IBM Plex Mono, monospace",
                            fontSize: "12px",
                        },
                    }}
                />
            </LanguageProvider>
            </AuthProvider>
        </div>
    );
}

export default App;
